import React, { useEffect, useRef, useState } from 'react';
import {
    Badge,
    Button,
    Col,
    Dropdown,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import Highlighter from 'react-highlight-words';
import { useInfiniteQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { ReactComponent as ReactMoreIcon } from '../../../assets/icons/more.svg';
import NotificationIcon from '../../../constants/notificationIcon';
import { rowsPerPage } from '../../../constants/pagination';
import useIntersectionObserver from '../../../hooks/useIntersectionObserver';
import { StyledTh } from '../../../pages/Library/styles';
import LibraryHttpService from '../../../services/http/library-http';
import ConfirmationDialog from '../../../utils/ConfirmationDialog';
import handleResponseError from '../../../utils/handleResponseError';

interface Props {
    term: string;
    refresh: boolean;
    handleOpenModal: (id?: number) => void;
}

function Items({ term, refresh, handleOpenModal }: Props) {
    const itemOptions: any = {
        FAQ: 'FAQ',
        DOCUMENTATION: 'Documentação',
        VIDEO: 'Vídeo',
    };

    const loadMoreRef = useRef<HTMLDivElement | null>(null);

    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    const [showRemoveItemModal, setShowRemoveItemModal] =
        useState<boolean>(false);

    async function loadItems({ pageParam = 0 }: any): Promise<any> {
        const params = {
            term: term || '',
            skip: pageParam,
            take: rowsPerPage,
        };

        const { data } = await LibraryHttpService.getItems(params);

        return {
            data: data.data,
            currentPage: pageParam,
            pages: Math.ceil(data.total / rowsPerPage),
        };
    }

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isLoading,
        refetch,
        isFetchingNextPage,
    } = useInfiniteQuery('items', loadItems, {
        // eslint-disable-next-line consistent-return
        getNextPageParam: (response) => {
            if (response.currentPage + 1 < response.pages) {
                return response.currentPage + 1;
            }
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const remove = useMutation(
        async () => {
            if (!selectedItemId) {
                throw new Error('Item não encontrado para exclusão');
            }

            LibraryHttpService.deleteItem(selectedItemId);
        },
        {
            onError: (error: any) => {
                handleResponseError(
                    error,
                    'Ocorreu um erro ao excluir o item.',
                );
            },
            onSuccess: () => {
                toast.success('Item removido com sucesso');
            },
            onSettled: () => {
                refetch();
            },
        },
    );

    useIntersectionObserver({
        target: loadMoreRef,
        onIntersect: fetchNextPage,
        enabled: hasNextPage,
    });

    useEffect(() => {
        if (refresh) {
            refetch();
            return;
        }

        const timer = setTimeout(() => {
            refetch();
        }, 500);

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, term]);

    return (
        <Row className="pl-2 pr-2">
            <Col>
                <Table bordered hover size="sm" className="text-center">
                    <thead>
                        <tr>
                            <StyledTh>Título</StyledTh>
                            <StyledTh>Descrição</StyledTh>
                            <StyledTh>Biblioteca</StyledTh>
                            <StyledTh>Tipo</StyledTh>
                            <StyledTh>Módulo</StyledTh>
                            <StyledTh>Marca</StyledTh>
                            <StyledTh />
                        </tr>
                    </thead>
                    <tbody>
                        {data?.pages &&
                            data?.pages.map((group: any) =>
                                group?.data.map((item: any, index: any) => (
                                    <tr key={index}>
                                        <td>
                                            <Highlighter
                                                autoEscape
                                                highlightClassName="highlight-term"
                                                searchWords={[term]}
                                                textToHighlight={item.title}
                                            />
                                        </td>
                                        <td>
                                            <Highlighter
                                                autoEscape
                                                highlightClassName="highlight-term"
                                                searchWords={[term]}
                                                textToHighlight={
                                                    // eslint-disable-next-line no-nested-ternary
                                                    item.description
                                                        ? item.description
                                                              .length > 120
                                                            ? `${item.description.substring(
                                                                  0,
                                                                  120,
                                                              )}...`
                                                            : item.description
                                                        : ''
                                                }
                                            />
                                        </td>
                                        <td>
                                            <h5 className="m-2">
                                                <Badge
                                                    style={{
                                                        margin: '1%',
                                                        backgroundColor:
                                                            'rgba(47, 204, 139, 0.1)',
                                                        color: '#2fcc8b',
                                                    }}
                                                    variant="sucess"
                                                >
                                                    <Highlighter
                                                        autoEscape
                                                        searchWords={[term]}
                                                        highlightClassName="highlight-term"
                                                        textToHighlight={
                                                            item.library &&
                                                            item.library.title
                                                        }
                                                    />
                                                </Badge>
                                            </h5>
                                        </td>
                                        <td>
                                            <h5 className="m-2">
                                                <Badge
                                                    style={{
                                                        margin: '1%',
                                                        backgroundColor:
                                                            'rgba(47, 204, 139, 0.1)',
                                                        color: '#2fcc8b',
                                                    }}
                                                    variant="sucess"
                                                >
                                                    <Highlighter
                                                        autoEscape
                                                        searchWords={[term]}
                                                        highlightClassName="highlight-term"
                                                        textToHighlight={
                                                            item.library &&
                                                            itemOptions[
                                                                item.library
                                                                    .type
                                                            ]
                                                        }
                                                    />
                                                </Badge>
                                            </h5>
                                        </td>
                                        <td>
                                            {item.module && (
                                                <h5 className="m-2">
                                                    <Badge
                                                        style={{
                                                            margin: '1%',
                                                            backgroundColor:
                                                                'rgba(47, 204, 139, 0.1)',
                                                            color: '#2fcc8b',
                                                        }}
                                                        variant="sucess"
                                                    >
                                                        <Highlighter
                                                            autoEscape
                                                            highlightClassName="highlight-term"
                                                            searchWords={[]}
                                                            textToHighlight={
                                                                item.module.name
                                                            }
                                                        />
                                                    </Badge>
                                                </h5>
                                            )}
                                        </td>
                                        <td>
                                            {item.brand && (
                                                <h5 className="m-2">
                                                    <Badge
                                                        style={{
                                                            margin: '1%',
                                                            backgroundColor:
                                                                'rgba(47, 204, 139, 0.1)',
                                                            color: '#2fcc8b',
                                                        }}
                                                        variant="sucess"
                                                    >
                                                        <Highlighter
                                                            highlightClassName="highlight-term"
                                                            searchWords={[]}
                                                            textToHighlight={
                                                                item.brand.name
                                                            }
                                                        />
                                                    </Badge>
                                                </h5>
                                            )}
                                        </td>
                                        <td>
                                            <Dropdown key="left">
                                                <Dropdown.Toggle
                                                    bsPrefix="nexen"
                                                    className="mt-n3 mb-n3"
                                                    as={Button}
                                                    variant="text"
                                                >
                                                    <ReactMoreIcon
                                                        fill="#bdbdbd"
                                                        width="10"
                                                        height="20"
                                                    />
                                                </Dropdown.Toggle>

                                                <Dropdown.Menu>
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            handleOpenModal(
                                                                item.id,
                                                            )
                                                        }
                                                    >
                                                        Editar
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={() => {
                                                            setSelectedItemId(
                                                                item.id,
                                                            );
                                                            setShowRemoveItemModal(
                                                                true,
                                                            );
                                                        }}
                                                    >
                                                        Remover
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                )),
                            )}
                    </tbody>
                </Table>
            </Col>

            <Col md={12} className="text-center" ref={loadMoreRef as any}>
                {(isLoading || isFetchingNextPage) && (
                    <Col md={12} className="text-center">
                        <Spinner animation="border" />
                    </Col>
                )}
                {data?.pages[0].data.length === 0 && (
                    <Col md={12} className="text-center">
                        <strong style={{ color: '#adadad' }}>
                            Sem itens para carregar
                        </strong>
                    </Col>
                )}
            </Col>

            {showRemoveItemModal && (
                <ConfirmationDialog
                    show={showRemoveItemModal}
                    onHide={() => {
                        setShowRemoveItemModal(false);
                        setSelectedItemId(null);
                    }}
                    onConfirm={async () => {
                        await remove.mutateAsync();
                        setTimeout(() => {
                            refetch();
                        }, 500);
                    }}
                    icon={NotificationIcon.Warning}
                    title="Excluir item"
                    text="Deseja excluir o item selecionado?"
                />
            )}
        </Row>
    );
}

export default Items;
